import { ScrollingModule } from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  OnInit,
  Signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { routerSegments } from '@consts/routes';
import { features } from '@consts/text.const';
import { SharedEffects } from '@effects/shared.effects';
import { PreloaderColor } from '@enums/shared.enum';
import { WorkflowModel } from '@models/workflow.model';
import { ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { SharedActions } from '@states/shared/shared.action-types';
import { WorkflowActions } from '@states/workflow/workflow.action.types';
import { WorkflowsSelectors } from '@states/workflow/workflow.selector-types';
import { UiButtonComponent, UiDatasetTableComponent, UiHeaderWithBreadcrumbsComponent, UiToggleComponent, UiUserSettingsDateComponent } from '@ui';
import { filter, Observable, tap } from 'rxjs';
import { syncQueryParam } from 'src/app/helpers/sync-query-param.helper';
import { ConfirmModalModel } from 'src/app/services/confirm-dialogs/confirm-modal.model';
import { ConfirmDialogModel, UiNoDataComponent } from '@ui';
import { UiPreloaderComponent } from 'src/app/shared/ui-kit/ui-preloader/ui-preloader.component';
import { UiSearchComponent } from 'src/app/shared/ui-kit/ui-search/ui-search.component';
import { createWorkflowTableConfig, WorkflowCellContext, WorkflowTableRow } from './workflows-tabl.util';

@Component({
  selector: 'app-workflows-table',
  imports: [
    UiButtonComponent,
    TranslatePipe,
    UiPreloaderComponent,
    UiNoDataComponent,
    ScrollingModule,
    UiSearchComponent,
    UiToggleComponent,
    FormsModule,
    UiHeaderWithBreadcrumbsComponent,
    UiDatasetTableComponent,
    ReactiveFormsModule
  ],
  templateUrl: './workflows-table.component.html',
  styleUrl: './workflows-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowsTableComponent implements OnInit {
  public loaderColor = PreloaderColor;
  public noResults = features.workflows.noResults;
  public itemSize = 72;

  protected search = syncQueryParam<string>('search');

  fc = new FormControl(true);

  public selectWorkflowsSig: Signal<WorkflowModel.WorkflowDocument[]> =
    this.store$.selectSignal(WorkflowsSelectors.selectWorkflows);
  public selectissLoadingSig: Signal<boolean> = this.store$.selectSignal(
    WorkflowsSelectors.selectIsLoading,
  );
  public selectOrganizationSig = this.store$.selectSignal(
    OrganizationSelectors.selectActiveOrganization,
  );

  public selectCreatedGraph$: Observable<WorkflowModel.GetGraphResponse> =
    this.store$.select(WorkflowsSelectors.selectCreatedGraph);

  protected readonly toggleCellTemplate = viewChild<TemplateRef<WorkflowCellContext>>('toggleCell');
  protected readonly publishedCellTemplate = viewChild<TemplateRef<WorkflowCellContext>>('publishedCell');

  public breadcrumbs = [{ name: this.translateService.instant('BREADCRUMBS.WORKFLOWS') }];

  public readonly tableConfig = computed(() => {
    const toggleCellTemplate = this.toggleCellTemplate();
    const publishedCellTemplate = this.publishedCellTemplate();

    return createWorkflowTableConfig({
      toggleCellTemplate: toggleCellTemplate,
      publishedCellTemplate: publishedCellTemplate,
    });
  });

  public rowsSig = computed(() => {
    const workflows = this.selectWorkflowsSig()

    const rows: WorkflowTableRow[] = workflows?.map((workflow) => {
      return {
        enabled: workflow.enabled !== undefined ? workflow.enabled : true,
        name: workflow.preview.name,
        published: !!workflow.published,
        updatedAt: workflow.createdAt,
        createddAt: workflow.updatedAt,
        graphId: workflow.graphId,
      }
    })

    return rows;
  })

  constructor(
    private store$: Store,
    private sharedEffects$: SharedEffects,
    private destroyRef: DestroyRef,
    private translateService: TranslateService,
    private router: Router,
  ) { }

  public ngOnInit(): void {
    this.store$.dispatch(
      WorkflowActions.getWorkflows({
        orgId: this.selectOrganizationSig().orgId,
      }),
    );

    this.sharedEffects$.confirmation$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofType(SharedActions.showConfirmModalResultConfirm),
        filter(
          (x) => x.params['type'] === ConfirmModalModel.Action.removeWorkflow,
        ),
        tap((x) => {
          this.store$.dispatch(
            WorkflowActions.removeWorkflow({
              id: x.params['id'],
              orgId: this.selectOrganizationSig().orgId,
            }),
          );
        }),
      )
      .subscribe();
  }

  public onRowAction(event: {
    actionId: string;
    row: WorkflowTableRow;
  }): void {
    switch (event.actionId) {
      case 'edit':
        this.edit(event.row.graphId);
        break;
      case 'delete':
        this.delete(event.row.graphId, event.row.name);
        break;
      case 'viewRunList':
        this.goToRunList(event.row.graphId);
        break;
    }
  }

  public changeWorkflowStatus(status: boolean, graphId: string) {
    if (status) {
      this.store$.dispatch(WorkflowActions.enableWorkflow({
        orgId: this.selectOrganizationSig().orgId,
        graphId: graphId,
      }))
    } else {
      this.store$.dispatch(WorkflowActions.disableWorkflow({
        orgId: this.selectOrganizationSig().orgId,
        graphId: graphId,
      }))
    }
  }

  public edit(id: string): void {
    void this.router.navigate([`${routerSegments.workflows}`, id]);
  }

  public create(): void {
    const initGraphReq: WorkflowModel.InitGraphDto = {
      orgId: this.selectOrganizationSig().orgId,
    };

    this.store$.dispatch(WorkflowActions.initGraph({ graph: initGraphReq }));

    this.selectCreatedGraph$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((graph) => {
        if (graph) {
          void this.router.navigate([routerSegments.workflows, graph.graphId]);
        }
      });
  }

  public delete(id: string, name: string): void {
    this.store$.dispatch(
      SharedActions.showConfirmModal({
        options: {
          type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
          title: this.translateService.instant('FLOW_CHART.DELETE_MODAL.TITLE'),
          msg: this.translateService.instant(
            'FLOW_CHART.DELETE_MODAL.MESSAGE',
            { name: `"${name}"` },
          ),
          confirm: this.translateService.instant('COMMON.DELETE'),
          disableClose: true,
          params: {
            type: ConfirmModalModel.Action.removeWorkflow,
            id: id,
          },
        },
      }),
    );
  }

  private goToRunList(graphId: string) {
    void this.router.navigate([routerSegments.workflows, graphId, routerSegments.runs]);
  }
}
